import Box from '@mui/material/Box';
import {
  endOfMonth,
  endOfYear,
  format,
  startOfMonth,
  startOfYear,
  subDays,
  subMonths,
  subYears,
} from 'date-fns';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { DateRangeFields } from './date-range-fields';
import { FilterMenu } from './filter-menu';

/** The old dashboard's analytics period presets, in its order ("All time" = no date filter). */
export const DATE_PRESETS = [
  'allTime',
  'lastMonth',
  'currentMonth',
  'lastYear',
  'currentYear',
  'last3Months',
  'last6Months',
  'custom',
] as const;
export type DatePreset = (typeof DATE_PRESETS)[number];
type FixedPreset = Exclude<DatePreset, 'allTime' | 'custom'>;

const FIXED_PRESETS = DATE_PRESETS.filter(
  (preset): preset is FixedPreset => preset !== 'allTime' && preset !== 'custom',
);

const iso = (date: Date) => format(date, 'yyyy-MM-dd');

/**
 * `yyyy-MM-dd` bounds of a preset, as production computed them: "last N months" are the N full
 * calendar months before the current one, like "last month" leaves the current month out.
 */
export function presetRange(preset: FixedPreset, reference = new Date()) {
  const range = (start: Date, end: Date) => ({ start: iso(start), end: iso(end) });
  switch (preset) {
    case 'lastMonth': {
      const month = subMonths(reference, 1);
      return range(startOfMonth(month), endOfMonth(month));
    }
    case 'currentMonth':
      return range(startOfMonth(reference), endOfMonth(reference));
    case 'lastYear': {
      const year = subYears(reference, 1);
      return range(startOfYear(year), endOfYear(year));
    }
    case 'currentYear':
      return range(startOfYear(reference), endOfYear(reference));
    case 'last3Months':
      return range(startOfMonth(subMonths(reference, 3)), endOfMonth(subMonths(reference, 1)));
    case 'last6Months':
      return range(startOfMonth(subMonths(reference, 6)), endOfMonth(subMonths(reference, 1)));
  }
}

/** Which preset a URL range corresponds to; anything else is a custom range. */
export function detectPreset(start: string, end: string, reference = new Date()): DatePreset {
  if (!start && !end) return 'allTime';
  const match = FIXED_PRESETS.find((preset) => {
    const range = presetRange(preset, reference);
    return range.start === start && range.end === end;
  });
  return match ?? 'custom';
}

interface DateRangePresetFieldProps {
  /** `yyyy-MM-dd`; both empty means "all time". */
  start: string;
  end: string;
  /** Called with both bounds at once (both empty for "all time"), never with just one. */
  onChange: (start: string, end: string) => void;
  label?: string;
  /** Latest day a custom range may end on; defaults to yesterday, as in the old dashboard. */
  max?: string;
  disabled?: boolean;
}

/**
 * Period filter of the old analytics screens: a preset menu and, for "Custom range", a from/to
 * pair. A custom range is only applied once both dates are set and in order, because the report
 * endpoints reject a range with one open end.
 */
export function DateRangePresetField({
  start,
  end,
  onChange,
  label,
  max,
  disabled,
}: DateRangePresetFieldProps) {
  const { t } = useTranslation();
  const detected = detectPreset(start, end);
  const [custom, setCustom] = useState(detected === 'custom');
  const [draft, setDraft] = useState({ start, end });

  useEffect(() => {
    setDraft({ start, end });
    if (start || end) setCustom((current) => current || detectPreset(start, end) === 'custom');
  }, [start, end]);

  const preset: DatePreset = custom ? 'custom' : detected;
  const latest = max ?? iso(subDays(new Date(), 1));

  const choose = (value: DatePreset) => {
    if (value === 'custom') {
      setCustom(true);
      return;
    }
    setCustom(false);
    if (value === 'allTime') onChange('', '');
    else {
      const range = presetRange(value);
      onChange(range.start, range.end);
    }
  };

  const edit = (next: { start: string; end: string }) => {
    setDraft(next);
    if (next.start && next.end && next.start <= next.end) onChange(next.start, next.end);
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'center',
        gap: 1.25,
        minWidth: 0,
        maxWidth: '100%',
      }}
    >
      <FilterMenu
        label={label ?? t('range.label')}
        value={preset}
        onChange={choose}
        disabled={disabled}
        options={DATE_PRESETS.map((value) => ({ value, label: t(`range.presets.${value}`) }))}
      />
      {preset === 'custom' && (
        <DateRangeFields
          start={draft.start}
          end={draft.end}
          onStartChange={(value) => edit({ ...draft, start: value })}
          onEndChange={(value) => edit({ ...draft, end: value })}
          max={latest}
          disabled={disabled}
        />
      )}
    </Box>
  );
}
